/**
 * Custom Option Fees
 * 
 * Handles adding fee products to cart when custom options are selected.
 * Fee products are linked to their parent via hidden properties.
 */

import { ThemeEvents, CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class CustomOptionFeesManager {
  constructor() {
    this.pendingFees = new Map();
    this.processing = false;
    this.processedEvents = new Set(); // Track processed events to prevent duplicates
    this.init();
  }

  init() {
    // Only attach to product forms directly (not document/body to avoid duplicates)
    this.attachToProductForms();
    
    document.addEventListener('shopify:section:load', () => {
      // Re-attach to product forms after section reload
      this.attachToProductForms();
      this.pendingFees.clear();
    });
  }

  /**
   * Attach event listeners directly to product form elements
   */
  attachToProductForms() {
    const productForms = document.querySelectorAll('product-form-component');
    
    productForms.forEach(form => {
      if (form.dataset.feesAttached === 'true') {
        return; // Skip if already attached
      }
      
      // Create a bound handler so we can reference it later
      const handler = this.handleCartAdd.bind(this);
      form.addEventListener(ThemeEvents.cartUpdate, handler, { once: false });
      form.dataset.feesAttached = 'true';
    });
  }

  /**
   * Handle cart add event and check for fees
   * @param {CartAddEvent} event 
   */
  async handleCartAdd(event) {
    // Only process if this is a product-form add (not other cart updates)
    if (event.detail.data?.source !== 'product-form-component') {
      return;
    }
    if (event.detail.data?.didError) {
      return;
    }

    const productId = event.detail.data?.productId;
    const quantity = event.detail.data?.itemCount || 1;
    
    if (!productId) {
      return;
    }

    // Create a unique identifier for this cart add event
    const eventId = `${productId}-${Date.now()}`;
    
    // Check if we've already processed this event (within the last second)
    if (this.processedEvents.has(eventId)) {
      return;
    }
    
    if (this.processing) {
      return;
    }
    
    // Mark this event as processed
    this.processedEvents.add(eventId);
    // Clean up old processed events after 2 seconds
    setTimeout(() => {
      this.processedEvents.delete(eventId);
    }, 2000);

    // Find the product form by productId
    // If there are multiple forms with the same productId (e.g., modal + product card),
    // prioritize forms within a modal (quick-add-dialog)
    const allForms = document.querySelectorAll(`product-form-component[data-product-id="${productId}"]`);
    let productForm = null;
    
    // First, check if any form is within a modal
    for (const form of allForms) {
      if (form.closest('quick-add-dialog')) {
        productForm = form;
        break;
      }
    }
    
    // If no modal form found, use the first one (regular product page)
    if (!productForm && allForms.length > 0) {
      productForm = allForms[0];
    }
    
    if (!productForm) {
      return;
    }

    const form = productForm.querySelector('form');
    if (!form) {
      return;
    }

    // Collect all fee products that should be added
    const feesToAdd = await this.collectFees(form, productId, quantity);

    if (feesToAdd.length > 0) {
      this.processing = true;
      await this.addFeesToCart(feesToAdd);
      this.processing = false;
    }
  }

  /**
   * Collect all fees that should be added based on form values
   * @param {HTMLFormElement} form 
   * @param {string} parentProductId 
   * @param {number} productQuantity - Quantity of the parent product being added
   * @returns {Promise<Array<{variantId: string, quantity: number, properties: Object}>>}
   */
  async collectFees(form, parentProductId, productQuantity) {
    const fees = [];
    const formData = new FormData(form);

    // Check all custom option blocks for fees
    const blocks = form.querySelectorAll('[data-fee-config]');
    
    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      const feeConfig = this.parseFeeConfig(block);
      const blockType = block.dataset.feeType;
      const propertyKey = block.dataset.key || block.dataset.feePropertyKey;
      
      if (!feeConfig || !feeConfig.enabled) {
        continue;
      }

      switch (blockType) {
        case 'checkbox': {
          // For checkboxes, check the checkbox element directly
          const checkbox = block.querySelector('input[type="checkbox"]');
          
          if (checkbox && checkbox.checked) {
            const value = checkbox.value || 'checked';
            const fee = await this.createFee(feeConfig, parentProductId, propertyKey, value, productQuantity);
            if (fee) fees.push(fee);
          }
          break;
        }
        
        case 'text': {
          // For text inputs, check if there's a value
          const value = formData.get(`properties[${propertyKey}]`);
          
          if (value && value.toString().trim() !== '') {
            const fee = await this.createFee(feeConfig, parentProductId, propertyKey, value, productQuantity);
            if (fee) fees.push(fee);
          }
          break;
        }

        case 'select':
        case 'radio': {
          // For select/radio, check if selected option has a fee
          const value = formData.get(`properties[${propertyKey}]`);
          if (value && feeConfig.optionFees) {
            const lookupKey = value.toString().toLowerCase().trim();
            const optionFee = feeConfig.optionFees[lookupKey];
            if (optionFee) {
              const fee = await this.createFee(optionFee, parentProductId, propertyKey, value, productQuantity);
              if (fee) fees.push(fee);
            }
          }
          break;
        }

        case 'multiselect':
        case 'swatch-checkbox': {
          // For multiselect/checkboxes, check each selected option
          const value = formData.get(`properties[${propertyKey}]`);
          if (value && feeConfig.optionFees) {
            // Value is comma-separated list
            const selectedOptions = value.toString().split(',').map(v => v.trim().toLowerCase());
            for (const option of selectedOptions) {
              if (option && feeConfig.optionFees[option]) {
                const fee = await this.createFee(
                  feeConfig.optionFees[option],
                  parentProductId,
                  propertyKey,
                  option,
                  productQuantity
                );
                if (fee) {
                  fees.push(fee);
                }
              }
            }
          }
          break;
        }

        case 'file': {
          // For file upload, check if a file was selected
          const fileInput = block.querySelector('input[type="file"]');
          if (fileInput?.files?.length > 0) {
            const fee = await this.createFee(feeConfig, parentProductId, propertyKey, fileInput.files.length + ' file(s)', productQuantity);
            if (fee) {
              fees.push(fee);
            }
          }
          break;
        }
      }
    }

    return fees;
  }

  /**
   * Parse fee configuration from block element
   * @param {HTMLElement} block 
   * @returns {Object|null}
   */
  parseFeeConfig(block) {
    try {
      const rawConfig = block.dataset.feeConfig || '{}';
      const config = JSON.parse(rawConfig);
      return config;
    } catch (e) {
      return null;
    }
  }

  /**
   * Create a fee object from configuration
   * @param {Object} config - Fee configuration object
   * @param {string} parentProductId 
   * @param {string} propertyKey 
   * @param {string} optionValue 
   * @param {number} productQuantity - Quantity of parent product being added
   * @returns {Promise<Object|null>}
   */
  async createFee(config, parentProductId, propertyKey, optionValue, productQuantity) {
    // Handle both old format (string) and new format (object)
    let productHandle, feeType, minQtyWaive;
    
    if (typeof config === 'string') {
      // Old format: just the product handle
      productHandle = config;
      feeType = 'one-time';
      minQtyWaive = null;
    } else {
      // New format: object with handle, type, and optional minQtyWaive
      productHandle = config.handle || config.productHandle;
      feeType = (config.type || 'one-time').toLowerCase().trim();
      minQtyWaive = config.minQtyWaive || null;
    }
    
    if (!productHandle) return null;

    // Check if fee should be waived based on quantity threshold
    if (minQtyWaive) {
      const threshold = parseInt(minQtyWaive, 10);
      if (!isNaN(threshold) && productQuantity >= threshold) {
        return null; // Skip this fee
      }
    }

    try {
      // Fetch the product to get its variant ID
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) {
        return null;
      }

      const product = await response.json();
      
      if (!product.variants || product.variants.length === 0) {
        return null;
      }

      // Use first available variant
      const variant = product.variants[0];

      // Calculate fee quantity based on type (case-insensitive)
      let feeQuantity = 1;
      if (feeType === 'per-item') {
        feeQuantity = productQuantity;
      }

      const feeItem = {
        id: variant.id,
        quantity: feeQuantity
      };
      
      return feeItem;
    } catch (error) {
      return null;
    }
  }

  /**
   * Add multiple fees to cart
   * @param {Array} fees 
   */
  async addFeesToCart(fees) {
    // Add fees one by one to avoid race conditions
    for (const fee of fees) {
      try {
        await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items: [fee] })
        });
      } catch (error) {
        // Silently fail to avoid blocking
      }
    }
    
    // Trigger a cart refresh by fetching the updated cart
    try {
      await fetch(window.Shopify?.routes?.root + 'cart.js');
      
      // Find all cart-items components and trigger their section refresh
      document.querySelectorAll('cart-items-component').forEach((cartItems) => {
        if (cartItems.dataset.sectionId) {
          // Import and use sectionRenderer to refresh the cart
          import('@theme/section-renderer').then(({ sectionRenderer }) => {
            sectionRenderer.renderSection(cartItems.dataset.sectionId, { cache: false });
          });
        }
      });
    } catch (error) {
      // Silently fail to avoid blocking
    }
  }
}

// Initialize the manager after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!window.customOptionFeesManager) {
      window.customOptionFeesManager = new CustomOptionFeesManager();
    }
  });
} else {
  if (!window.customOptionFeesManager) {
    window.customOptionFeesManager = new CustomOptionFeesManager();
  }
}

